/**
 * SubscriptionPlansView - Visualização de planos de assinatura
 * 
 * Extraído do App.js durante refatoração Fase 2
 * 
 * Funcionalidades:
 * - Listagem de planos disponíveis
 * - Visualização do plano atual
 * - Assinatura de novo plano
 * - Itens avulsos (destaques, anúncios extras)
 * - Diferenciação entre profissional e empregador
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - useAuth (autenticação)
 * - axios (requisições API)
 * - Card, Button, Badge (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import { FreeHighlightDialog } from "./FreeHighlightDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionPlansView = () => {
  const [professionalPlans, setProfessionalPlans] = useState([]);
  const [employerPlans, setEmployerPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showTip, setShowTip] = useState(true);
  const [currency, setCurrency] = useState('brl');
  const [freeHighlights, setFreeHighlights] = useState(null);
  const [showFreeHighlightDialog, setShowFreeHighlightDialog] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchPlansAndSubscription();
    fetchFreeHighlights();
  }, []);

  const fetchFreeHighlights = async () => {
    try {
      const res = await axios.get(`${API}/free-highlights/status`);
      setFreeHighlights(res.data);
    } catch {
      setFreeHighlights(null);
    }
  };

  const fetchPlansAndSubscription = async () => {
    try {
      setLoading(true);
      const [plansResponse, subscriptionResponse] = await Promise.all([
        axios.get(`${API}/subscription-plans?user_type=${user.user_type}`),
        axios.get(`${API}/my-subscription`)
      ]);
      
      const data = plansResponse.data;
      if (Array.isArray(data)) {
        // Single user type - classify by user_type field
        const profPlans = data.filter(p => p.user_type === 'professional');
        const empPlans = data.filter(p => p.user_type === 'employer');
        if (profPlans.length > 0) {
          setProfessionalPlans(profPlans);
        }
        if (empPlans.length > 0) {
          setEmployerPlans(empPlans);
        }
        // If all same type, put them in the right bucket
        if (profPlans.length === 0 && empPlans.length === 0) {
          if (user.user_type === 'professional') setProfessionalPlans(data);
          else setEmployerPlans(data);
        }
      } else if (data.professional_plans || data.employer_plans) {
        setProfessionalPlans(data.professional_plans || []);
        setEmployerPlans(data.employer_plans || []);
      }
      setCurrentPlan(subscriptionResponse.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId, billingCycle = 'monthly') => {
    const allPlans = [...professionalPlans, ...employerPlans];
    const plan = allPlans.find(p => p.id === planId);
    const isFreePlan = plan?.price_monthly === 0;

    if (isFreePlan) {
      try {
        const response = await axios.post(`${API}/subscribe/${planId}`, {}, {
          params: { billing_cycle: 'monthly' }
        });
        alert(response.data.message);
        fetchPlansAndSubscription();
      } catch (error) {
        alert(t('spErrorSubscription') + ": " + (error.response?.data?.detail || error.message));
      }
      return;
    }

    // Paid plan - redirect to Stripe Checkout
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        origin_url: originUrl,
        currency: currency,
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      alert(t('spErrorSubscription') + ": " + (error.response?.data?.detail || error.message));
      setCheckoutLoading(null);
    }
  };

  const handleBuyItem = async (itemType) => {
    setCheckoutLoading(itemType);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        item_type: itemType,
        origin_url: originUrl,
        currency: currency,
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      alert(t('spErrorSubscription') + ": " + (error.response?.data?.detail || error.message));
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8">{t('loadingPlans')}</div>;
  }

  const isProfessional = user?.user_type === 'professional';

  const renderPlanCard = (plan) => {
    const isCurrentPlan = currentPlan?.plan_id === plan.id;
    const isFreePlan = plan.price_monthly === 0;
    const isRecommended = plan.recommended;
    
    return (
      <Card key={plan.id} className={`relative ${isCurrentPlan ? 'ring-2 ring-green-500' : ''} ${isRecommended ? 'ring-2 ring-blue-500' : ''}`}>
        {isRecommended && !isCurrentPlan && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
            <Badge className="bg-blue-500 text-white px-3 py-1">{t('recommended')}</Badge>
          </div>
        )}
        {isCurrentPlan && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
            <Badge className="bg-green-500 text-white px-3 py-1">{t('currentPlan')}</Badge>
          </div>
        )}
        
        <CardHeader className="pt-6">
          <div className="text-center">
            <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
            <div className="mt-4">
              {isFreePlan ? (
                <div className="text-4xl font-bold text-green-600">{t('free')}</div>
              ) : (
                <div>
                  <div className="text-4xl font-bold text-gray-900">
                    {currency === 'usd' ? '$' : 'R$'} {(plan.price_monthly * (currency === 'usd' ? 0.20 : 1)).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-600">{t('perMonth')}</div>
                  {plan.price_yearly > 0 && (
                    <div className="mt-2 text-sm">
                      <span className="text-green-600 font-semibold">
                        {currency === 'usd' ? '$' : 'R$'} {(plan.price_yearly * (currency === 'usd' ? 0.20 : 1)).toFixed(2)}{t('perYear')}
                      </span>
                      <br />
                      <span className="text-xs text-gray-500">
                        ({t('savingsPercent')} {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">{plan.description}</p>
          </div>
        </CardHeader>
        
        <CardContent>
          <ul className="space-y-3">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className="text-sm text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
          
          <div className="mt-6">
            {isCurrentPlan ? (
              <Button className="w-full bg-green-600 text-white cursor-default" data-testid={`current-plan-${plan.id}`}>
                {t('currentPlan')}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button 
                  data-testid={`subscribe-monthly-${plan.id}`}
                  onClick={() => handleSubscribe(plan.id, 'monthly')}
                  disabled={checkoutLoading === plan.id}
                  className={`w-full flex items-center justify-center ${isFreePlan ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {checkoutLoading === plan.id ? t('redirectingToPayment') : (isFreePlan ? t('activateFree') : t('choosePlan'))}
                </Button>
                {!isFreePlan && plan.price_yearly > 0 && (
                  <Button 
                    data-testid={`subscribe-yearly-${plan.id}`}
                    onClick={() => handleSubscribe(plan.id, 'yearly')}
                    disabled={checkoutLoading === plan.id}
                    variant="outline"
                    className="w-full flex items-center justify-center border-green-500 text-green-700 hover:bg-green-50 text-xs sm:text-sm whitespace-normal h-auto py-2"
                  >
                    {t('choosePlan')} {t('perYear')} ({t('savingsPercent')} {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {showTip && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-purple-400 hover:text-purple-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-2 pr-6">
            <span className="text-lg">💎</span>
            <div>
              <h4 className="font-semibold text-purple-800">{t('howToUse')}</h4>
              <p className="text-sm text-purple-700 mt-1">{t('tooltipPlans')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Currency Selector */}
      <div className="flex justify-end" data-testid="currency-selector">
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-white">
          <button
            onClick={() => setCurrency('brl')}
            className={`px-3 py-1.5 text-sm rounded-md transition-all ${currency === 'brl' ? 'bg-green-100 text-green-800 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="currency-brl"
          >
            🇧🇷 R$ BRL
          </button>
          <button
            onClick={() => setCurrency('usd')}
            className={`px-3 py-1.5 text-sm rounded-md transition-all ${currency === 'usd' ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid="currency-usd"
          >
            🇺🇸 $ USD
          </button>
        </div>
      </div>

      {/* Professional Plans Section */}
      {professionalPlans.length > 0 && (
        <div>
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-testid="plans-professional-title">
              {t('plansForProfessionals')}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">{t('plansDescProfessional')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionalPlans.map(renderPlanCard)}
          </div>
        </div>
      )}

      {/* Employer Plans Section */}
      {employerPlans.length > 0 && (
        <div className={professionalPlans.length > 0 ? 'mt-10 pt-10 border-t border-gray-200' : ''}>
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-testid="plans-employer-title">
              {t('plansForEmployers')}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">{t('plansDescEmployer')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {employerPlans.map(renderPlanCard)}
          </div>
        </div>
      )}

      {/* Seção de Itens Avulsos */}
      <div className="mt-10 border-t pt-8">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">🛒 {t('individualItems')}</h3>
          <p className="text-gray-600 mt-2">{t('buyExtrasWithoutChangingPlan')}</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {/* Destaque */}
          <Card className="border-2 border-amber-200 hover:border-amber-400 transition-colors">
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-3">⭐</div>
              <h4 className="text-lg font-bold text-amber-700">{t('adHighlight')}</h4>
              <p className="text-3xl font-bold text-amber-600 my-3">{currency === 'usd' ? '$' : 'R$'} {currency === 'usd' ? '1.98' : '9,90'}</p>
              <p className="text-sm text-gray-600 mb-4">
                {t('adHighlightDesc')}
              </p>
              <ul className="text-xs text-gray-500 space-y-1 mb-4 text-left">
                <li>✓ {t('moreVisibility')}</li>
                <li>✓ {t('highlightBadge')}</li>
                <li>✓ {t('validFor7Days')}</li>
              </ul>
              {freeHighlights && freeHighlights.monthly_quota > 0 && (
                freeHighlights.available > 0 ? (
                  <div className="bg-green-50 border border-green-300 rounded-lg p-3 mb-3" data-testid="free-highlight-banner">
                    <p className="text-sm font-semibold text-green-800">
                      ✨ {t('freeHighlightAvailablePrefix')} {freeHighlights.available} {t('freeHighlightAvailableSuffix')}
                    </p>
                    <Button
                      data-testid="use-free-highlight-btn"
                      className="w-full mt-2 bg-green-600 hover:bg-green-700"
                      onClick={() => setShowFreeHighlightDialog(true)}
                    >
                      {t('useFreeHighlight')}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3" data-testid="free-highlight-used-banner">
                    <p className="text-xs text-gray-600">
                      {t('freeHighlightUsed')} · {t('renewsOn')} {freeHighlights.next_reset}
                    </p>
                  </div>
                )
              )}
              <Button 
                data-testid="buy-highlight-btn"
                className="w-full bg-amber-500 hover:bg-amber-600"
                disabled={checkoutLoading === 'highlight'}
                onClick={() => handleBuyItem('highlight')}
              >
                {checkoutLoading === 'highlight' ? t('redirectingToPayment') : t('buyHighlightBtn')}
              </Button>
            </CardContent>
          </Card>
          
          {/* Anúncio Balcão */}
          <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-3">📋</div>
              <h4 className="text-lg font-bold text-blue-700">{t('extraAdJobBoard')}</h4>
              <p className="text-3xl font-bold text-blue-600 my-3">{currency === 'usd' ? '$' : 'R$'} {currency === 'usd' ? '1.98' : '9,90'}</p>
              <p className="text-sm text-gray-600 mb-4">
                {t('extraAdJobBoardDesc')}
              </p>
              <ul className="text-xs text-gray-500 space-y-1 mb-4 text-left">
                <li>✓ {t('validFor30Days')}</li>
                <li>✓ {t('noChangeToPlan')}</li>
                <li>✓ {t('immediateUse')}</li>
              </ul>
              <Button 
                data-testid="buy-ad-jobboard-btn"
                className="w-full bg-blue-500 hover:bg-blue-600"
                disabled={checkoutLoading === 'ad_job_board'}
                onClick={() => handleBuyItem('ad_job_board')}
              >
                {checkoutLoading === 'ad_job_board' ? t('redirectingToPayment') : t('buyAdBtn')}
              </Button>
            </CardContent>
          </Card>
          
          {/* Anúncio Marketplace */}
          <Card className="border-2 border-green-200 hover:border-green-400 transition-colors">
            <CardContent className="p-6 text-center">
              <div className="text-4xl mb-3">🛒</div>
              <h4 className="text-lg font-bold text-green-700">{t('extraAdMarketplace')}</h4>
              <p className="text-3xl font-bold text-green-600 my-3">{currency === 'usd' ? '$' : 'R$'} {currency === 'usd' ? '1.98' : '9,90'}</p>
              <p className="text-sm text-gray-600 mb-4">
                {t('extraAdMarketplaceDesc')}
              </p>
              <ul className="text-xs text-gray-500 space-y-1 mb-4 text-left">
                <li>✓ {t('validFor30Days')}</li>
                <li>✓ {t('noChangeToPlan')}</li>
                <li>✓ {t('immediateUse')}</li>
              </ul>
              <Button 
                data-testid="buy-ad-marketplace-btn"
                className="w-full bg-green-500 hover:bg-green-600"
                disabled={checkoutLoading === 'ad_marketplace'}
                onClick={() => handleBuyItem('ad_marketplace')}
              >
                {checkoutLoading === 'ad_marketplace' ? t('redirectingToPayment') : t('buyAdBtn')}
              </Button>
            </CardContent>
          </Card>
        </div>
        
        <p className="text-center text-xs text-gray-400 mt-4">
          💳 {t('paymentViaPix')}
        </p>
      </div>

      {!isProfessional && (
        <div className="mt-8 text-center">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              {t('annualTipTitle')}
            </h3>
            <p className="text-blue-700 text-sm">
              {t('annualTipDesc')}
            </p>
          </div>
        </div>
      )}

      <FreeHighlightDialog
        open={showFreeHighlightDialog}
        onOpenChange={setShowFreeHighlightDialog}
        onUsed={fetchFreeHighlights}
      />
    </div>
  );
};

export default SubscriptionPlansView;
